import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  gameCategories as fetchCategories,
  GameCategory,
  Game,
  providers as fetchProviders,
  favoriteGames as fetchFavoriteGames,
  favoriteGamesIds as fetchFavoriteGamesIds,
  setFavoriteGame as fetchSetFavoriteGame,
  deleteFavoriteGame as fetchDeleteFavoriteGame,
  recentGames as fetchRecentGames,
  gamesFilterSelect as fetchGamesFilterSelect,
  GamesFilter,
  gamesFilterV2 as fetchGamesFilter,
  Provider,
} from '../../api/games';
import { useApi } from '../../hooks/api';
import { AuthContext } from '../auth';
import { isMobileDevice } from '../../helpers/common';
import { latestWinners, Winner } from '../../api/winners';
import { getGamePath, mapGameV2ToGame } from '../../helpers/game';
import { DEMO_CURRENCY } from 'helpers/constants';
import { CurrencyCode } from 'helpers/currency';

export interface GamesContextData {
  categories: GameCategory[];
  themes: GameCategory[];
  categoriesByRegEx: (regEx: RegExp) => GameCategory[];
  pendingCategories: boolean;
  providers: Provider[];
  pendingProviders: boolean;
  favoriteGames: Game[];
  favoriteGamesIds: number[];
  recentGames: Game[];
  winners: Winner[];
  pendingWinners: boolean;
  pendingFavoriteGames: boolean;
  pendingRecentGames: boolean;
  fetchGamesV2: (
    body?: Omit<GamesFilter.Body, 'currencies'>
  ) => Promise<GamesFilter.ResponseV2 | undefined>;
  getRecentGames: () => Promise<void>;
}

export interface GamesContextAction {
  addFavoriteGames: (currencyCode: Game['real']) => Promise<void>;
  removeFavoriteGames: (currencyCode: Game['real']) => Promise<void>;
}

export const GamesContext = createContext<
  GamesContextData & GamesContextAction
>(null as any);

export const GamesContextProvider: FC = ({ children }) => {
  const {
    i18n: { language },
  } = useTranslation();

  const { isAuthenticated, user, wallets, pendingUser } = useContext(
    AuthContext
  );

  const [favoriteGamesIds, setFavoriteGamesIds] = useState<number[]>([]);
  const [favoriteGames, setFavoriteGames] = useState<Game[]>([]);
  const [recentGames, setRecentGames] = useState<Game[]>([]);
  const [pendingFavoriteGames, setPendingFavoriteGames] = useState(true);
  const [pendingRecentGames, setPendingRecentGames] = useState(true);

  const [
    requestGameCategories,
    { data: dataCategories, pending: pendingCategories },
  ] = useApi(fetchCategories);

  const [
    requestProviders,
    { data: providers = [], pending: pendingProviders },
  ] = useApi(fetchProviders);

  const [
    requestWinners,
    { data: dataWinners, pending: pendingWinners },
  ] = useApi(latestWinners);

  useEffect(() => {
    if (language != null) {
      requestGameCategories(language);
    }
  }, [requestGameCategories, language]);

  useEffect(() => {
    requestProviders();
    requestWinners();
  }, [requestProviders, requestWinners]);

  const fetchFavoriteGame = useCallback(async () => {
    try {
      const { data: favoriteGames } = await fetchFavoriteGames();
      const { data: favoriteGamesIds } = await fetchFavoriteGamesIds();

      if (favoriteGamesIds && favoriteGames) {
        const favoriteGamesUpdated = mapGameV2ToGame(favoriteGames);
        setFavoriteGamesIds(favoriteGamesIds);
        setFavoriteGames(favoriteGamesUpdated);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPendingFavoriteGames(false);
    }
  }, [isAuthenticated]);

  const getRecentGames = useCallback(async () => {
    try {
      const response = await fetchRecentGames();
      if (!response) return;
      const gameIds = response.data.map(({ identifier }) => identifier);
      const responseGames = await fetchGamesSelectV2({ gameIds });

      if (!responseGames) return;
      setRecentGames(mapGameV2ToGame(Object.values(responseGames)));
    } catch (error) {
      console.error(error);
    } finally {
      setPendingRecentGames(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated && !pendingUser) {
      setPendingFavoriteGames(false);
      setPendingRecentGames(false);
      return;
    }

    fetchFavoriteGame();
    getRecentGames();
  }, [fetchFavoriteGame, getRecentGames, isAuthenticated, pendingUser]);

  const categories = useMemo(() => {
    if (!dataCategories) return [];

    return dataCategories.filter(
      ({ id }) => /^_top_/.test(id) || (!/^_/.test(id) && /^[a-z]/.test(id))
    );
  }, [dataCategories]);

  const themes = useMemo(() => {
    if (!dataCategories) return [];

    return dataCategories.filter(({ id }) => id.startsWith('@'));
  }, [dataCategories]);

  const categoriesByRegEx = useCallback(
    (regEx: RegExp) => {
      if (!dataCategories) return [];

      return dataCategories.filter(({ id }) => regEx.test(id));
    },
    [dataCategories]
  );

  const winners = useMemo(() => {
    if (!dataWinners) return [];

    return dataWinners.map((winner) => ({
      ...winner,
      path: getGamePath(winner.gameTitle),
    }));
  }, [dataWinners]);

  const addFavoriteGames = useCallback(
    async (currencies: Game['real']) => {
      if (!user?.currency || !currencies) return;

      const currencyCode = currencies[user.currency].id;

      try {
        await fetchSetFavoriteGame(currencyCode);
        await fetchFavoriteGame();
      } catch (error) {
        console.error(error);
      }
    },
    [user]
  );

  const removeFavoriteGames = useCallback(
    async (currencies: Game['real']) => {
      if (!currencies) return;

      const favoriteGameId = Object.values(currencies)
        .map(({ id }) => id)
        .find((c) => favoriteGamesIds.includes(c));

      if (!favoriteGameId) return;

      try {
        await fetchDeleteFavoriteGame(favoriteGameId);
        await fetchFavoriteGame();
      } catch (error) {
        console.error(error);
      }
    },
    [favoriteGames]
  );

  const accounts = useMemo(() => {
    return wallets.map(({ currency }) => currency) as CurrencyCode[];
  }, [wallets]);

  const fetchGamesV2 = useCallback(
    async (
      body?: Omit<GamesFilter.Body, 'currencies'>
    ): Promise<GamesFilter.ResponseV2 | undefined> => {
      try {
        const { data } = await fetchGamesFilter({
          device: isMobileDevice ? 'mobile' : 'desktop',
          currencies: isAuthenticated ? accounts : [DEMO_CURRENCY],
          sort: {
            type: 'popularity',
            direction: 'DESC',
          },
          ...body,
        });

        if (data) {
          const { data: games, pagination } = data;
          return {
            data: mapGameV2ToGame(games),
            pagination,
          };
        }
      } catch (error) {
        console.error(error);
      }
    },
    [isMobileDevice, isAuthenticated, accounts]
  );

  const fetchGamesSelectV2 = useCallback(
    async (
      body: GamesFilter.BodySelect
    ): Promise<GamesFilter.ResponseSelect | undefined> => {
      try {
        const res = await fetchGamesFilterSelect({
          device: isMobileDevice ? 'mobile' : 'desktop',
          ...body,
        });

        return res.data;
      } catch (error) {
        console.error(error);
      }
    },
    [isMobileDevice]
  );

  return (
    <GamesContext.Provider
      value={{
        categories,
        themes,
        categoriesByRegEx,
        pendingCategories,
        providers,
        pendingProviders,
        favoriteGames,
        favoriteGamesIds,
        addFavoriteGames,
        removeFavoriteGames,
        recentGames,
        winners,
        pendingWinners,
        pendingFavoriteGames,
        pendingRecentGames,
        fetchGamesV2,
        getRecentGames,
      }}
    >
      {children}
    </GamesContext.Provider>
  );
};
